/**
 * Helpers for chat reply preview name + body text.
 */
import type { ExtendedChatMessage, MessageReplyTo } from '../types/chat';
import type { TranslateFn } from '../types/common';

const isGenericUserLabel = (name?: string | null): boolean => {
  const trimmed = name?.trim();
  if (!trimmed) return true;
  return trimmed.toLowerCase() === 'user';
};

const normalizeReplyUser = (
  user?: ExtendedChatMessage['user'] | MessageReplyTo['user'] | null,
): MessageReplyTo['user'] => {
  if (!user) return undefined;
  const rawAvatar = user.avatar as unknown;
  const avatar =
    typeof rawAvatar === 'string' ||
    typeof rawAvatar === 'number' ||
    (rawAvatar && typeof rawAvatar === 'object')
      ? (rawAvatar as MessageReplyTo['user'] extends { avatar?: infer A } ? A : never)
      : null;
  return {
    _id: user._id,
    name: user.name,
    avatar,
  };
};

export const resolveReplySenderName = (
  reply?: MessageReplyTo | ExtendedChatMessage | null,
  fallbackName?: string | null,
): string => {
  if (!reply) {
    return fallbackName?.trim() || '';
  }

  const fromReply =
    ('senderName' in reply && reply.senderName?.trim()) ||
    reply.user?.name?.trim() ||
    '';

  if (fromReply && !isGenericUserLabel(fromReply)) {
    return fromReply;
  }

  const fallback = fallbackName?.trim() || '';
  if (fallback && !isGenericUserLabel(fallback)) {
    return fallback;
  }

  return fromReply || fallback || '';
};

const translateLabel = (
  t: TranslateFn | undefined,
  key: string,
  fallback: string,
): string => {
  if (!t) return fallback;
  const value = t(key);
  return !value || value === key ? fallback : value;
};

export const resolveReplyPreviewText = (
  reply?: MessageReplyTo | ExtendedChatMessage | null,
  t?: TranslateFn,
): string => {
  if (!reply) return '';

  const text = typeof reply.text === 'string' ? reply.text.trim() : '';
  if (text) return text;

  const type = (reply.type || '').toLowerCase();
  const fileName = 'fileName' in reply ? reply.fileName : undefined;
  const image = reply.image;
  const video =
    'video' in reply ? (reply as ExtendedChatMessage).video : undefined;

  if (type.includes('gif') || fileName?.toLowerCase().includes('.gif')) {
    return translateLabel(t, 'individualChat.attachment.gif', 'GIF');
  }
  if (image || type.includes('image') || type.includes('photo')) {
    return translateLabel(t, 'chat.photo', 'Photo');
  }
  if (video || type.includes('video')) {
    return translateLabel(t, 'individualChat.attachment.video', 'Video');
  }
  if (type.includes('audio') || type.includes('voice')) {
    return translateLabel(
      t,
      'individualChat.attachment.voice',
      'Voice Message',
    );
  }
  if (fileName || type.includes('file') || type.includes('document')) {
    return (
      fileName ||
      translateLabel(t, 'individualChat.attachment.document', 'Document')
    );
  }

  return '';
};

export interface EnrichReplyContextOptions {
  peerDisplayName?: string | null;
  selfDisplayName?: string | null;
  currentUserId?: string | number | null;
}

/** Prefer richer local reply metadata over sparse API replyContext. */
export const mergeReplyTo = (
  apiReply?: MessageReplyTo | null,
  localReply?: MessageReplyTo | null,
): MessageReplyTo | null => {
  if (!apiReply && !localReply) return null;

  const senderName =
    resolveReplySenderName(apiReply, localReply?.senderName) ||
    resolveReplySenderName(localReply) ||
    undefined;

  return {
    ...localReply,
    ...apiReply,
    _id: apiReply?._id ?? localReply?._id,
    text:
      (apiReply?.text && apiReply.text.trim()) ||
      (localReply?.text && localReply.text.trim()) ||
      '',
    image: apiReply?.image || localReply?.image,
    type: apiReply?.type || localReply?.type,
    fileName: apiReply?.fileName || localReply?.fileName,
    senderName,
    user: apiReply?.user || localReply?.user,
  };
};

/** Build replyTo from the message being replied to. */
export const buildReplyToFromMessage = (
  message: ExtendedChatMessage,
  options?: {
    peerDisplayName?: string | null;
    selfDisplayName?: string | null;
    currentUserId?: string | number | null;
  },
): MessageReplyTo => {
  const isOwn =
    options?.currentUserId != null &&
    String(message.user?._id) === String(options.currentUserId);
  const fallback = isOwn
    ? options?.selfDisplayName
    : options?.peerDisplayName;

  return {
    _id: message._id,
    text: message.text,
    image: message.image,
    type: message.type,
    fileName: message.fileName,
    senderName: resolveReplySenderName(message, fallback) || undefined,
    user: normalizeReplyUser(message.user),
  };
};

/** Fill incomplete replyTo fields from the original message in the same thread. */
export const enrichMessagesWithReplyContext = (
  messages: ExtendedChatMessage[],
  options?: EnrichReplyContextOptions | string | null,
): ExtendedChatMessage[] => {
  if (!messages.length) return messages;

  const opts: EnrichReplyContextOptions =
    typeof options === 'string' || options == null
      ? { peerDisplayName: options }
      : options;

  const byId = new Map(messages.map(m => [String(m._id), m]));

  return messages.map(message => {
    const reply = message.replyTo;
    if (!reply) return message;

    const original = reply._id != null ? byId.get(String(reply._id)) : undefined;
    const isOriginalMine =
      original != null &&
      opts.currentUserId != null &&
      String(original.user?._id) === String(opts.currentUserId);
    const fallbackName = isOriginalMine
      ? opts.selfDisplayName
      : opts.peerDisplayName;

    const senderName = resolveReplySenderName(
      {
        senderName: reply.senderName,
        user: normalizeReplyUser(reply.user || original?.user),
      },
      original?.user?.name || fallbackName,
    );
    const text =
      (reply.text && reply.text.trim()) || original?.text?.trim() || '';
    const image = reply.image || original?.image;
    const type = reply.type || original?.type;
    const fileName = reply.fileName || original?.fileName;

    const nextReply: MessageReplyTo = {
      ...reply,
      _id: reply._id ?? original?._id,
      text,
      image,
      type,
      fileName,
      senderName: senderName || undefined,
      user: normalizeReplyUser(reply.user || original?.user),
    };

    const unchanged =
      nextReply.text === reply.text &&
      nextReply.senderName === reply.senderName &&
      nextReply.image === reply.image &&
      nextReply.type === reply.type &&
      nextReply.fileName === reply.fileName;

    if (unchanged) return message;
    return { ...message, replyTo: nextReply };
  });
};
